import { useState, useEffect, useCallback } from 'react'
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Chip,
  Typography,
  Autocomplete,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import GroupIcon from '@mui/icons-material/Group'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { adventuresApi } from '@api/endpoints/adventures'
import { searchApi } from '@api/endpoints/search'
import { SearchEntityType, type UserSearchResult, type GroupSearchResult } from '@api/types/search'
import { useAuthStore } from '@store/authStore'
import type { UserSearchDto, RankDto } from '../../../types/adventure-editor.types'
import { RoleType } from '../../../types/adventure-editor.types'

// ====================
// TYPES
// ====================

export type TeamRoleType = 'Maintainer' | 'Moderator' | 'Tester'

// Unified search result type for users and groups
interface SearchResultItem {
  id: string
  type: 'user' | 'group'
  displayName: string
  profileImageUrl?: string
  // User-specific fields
  userId?: string
  rank?: {
    tier: number
    name: string
    badge?: string
  }
  // Group-specific fields
  groupId?: string
  memberCount?: number
  description?: string
}

interface TeamMember {
  id: string
  memberId: string // Either userId or groupId
  displayName: string
  profileImageUrl?: string
  isCreator?: boolean
  addedDate: string
  rank?: RankDto
  // Type discriminator
  isUser: boolean
  isGroup: boolean
  // Group-specific fields
  groupDescription?: string
  memberCount?: number
}

interface TeamManagementPanelProps {
  adventureId?: string
  roleType: TeamRoleType
  title: string
  description: string
  icon: React.ReactNode
}

// ====================
// HELPER FUNCTIONS
// ====================

function getPermissionsText(roleType: TeamRoleType): string {
  switch (roleType) {
    case 'Maintainer':
      return 'Can edit adventure details, manage locations, and configure settings.'
    case 'Moderator':
      return 'Can moderate user submissions, manage participants, and review content.'
    case 'Tester':
      return 'Can access and test the adventure before it is published to the public.'
  }
}

// ====================
// COMPONENT
// ====================

export function TeamManagementPanel({
  adventureId,
  roleType,
  title,
  description,
  icon,
}: TeamManagementPanelProps) {
  const currentUser = useAuthStore((state) => state.user)
  const currentUserId = currentUser?.userId || (currentUser as any)?.id
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load team members
  const loadTeamMembers = useCallback(async () => {
    if (!adventureId) return

    try {
      setIsLoading(true)
      setError(null)

      let members: any[] = []
      const roleTypeLower = roleType.toLowerCase()

      if (roleTypeLower === 'maintainer') {
        members = await adventuresApi.getMaintainers(adventureId)
      } else if (roleTypeLower === 'moderator') {
        members = await adventuresApi.getModerators(adventureId)
      } else if (roleTypeLower === 'tester') {
        members = await adventuresApi.getTesters(adventureId)
      }

      // Map the response to handle both users and groups
      const mappedMembers: TeamMember[] = (members || []).map((member: any) => {
        if (member.isGroup && member.group) {
          // Group member
          return {
            id: member.id,
            memberId: member.groupId,
            displayName: member.group.name,
            profileImageUrl: member.group.groupImage,
            isCreator: false,
            addedDate: member.addedDate,
            isUser: false,
            isGroup: true,
            groupDescription: member.group.description,
            memberCount: member.group.memberCount,
          }
        } else {
          // User member
          return {
            id: member.id,
            memberId: member.userId,
            displayName: member.user?.displayName || member.user?.displayNameOrFallback,
            profileImageUrl: member.user?.avatar || member.user?.profileImageUrl,
            isCreator: member.isCreator,
            addedDate: member.addedDate,
            rank: member.user?.rank,
            isUser: true,
            isGroup: false,
          }
        }
      })

      console.log('Mapped team members:', mappedMembers)
      setTeamMembers(mappedMembers)
    } catch (err) {
      console.error(`Error loading ${roleType}s:`, err)
      setError(`Failed to load ${roleType.toLowerCase()}s`)
    } finally {
      setIsLoading(false)
    }
  }, [adventureId, roleType])

  useEffect(() => {
    loadTeamMembers()
  }, [loadTeamMembers])

  // Search users (debounced) using global search API
  useEffect(() => {
    const searchUsers = async () => {
      if (!searchQuery || searchQuery.length < 2) {
        setSearchResults([])
        return
      }

      try {
        setIsSearching(true)
        const response = await searchApi.search({
          query: searchQuery,
          type: SearchEntityType.User | SearchEntityType.Group, // type=6: Users and Groups
          take: 10,
        })

        // Map users to SearchResultItem format
        const userResults: SearchResultItem[] = (response.users || []).map((user) => ({
          id: user.userId,
          type: 'user' as const,
          displayName: user.displayName,
          profileImageUrl: user.profilePicture || user.avatar,
          userId: user.userId,
          rank: user.rank ? {
            tier: user.rank.tier,
            name: user.rank.name,
            badge: user.rank.badge,
          } : undefined,
        }))

        // Map groups to SearchResultItem format
        const groupResults: SearchResultItem[] = (response.groups || []).map((group) => ({
          id: group.id,
          type: 'group' as const,
          displayName: group.name,
          profileImageUrl: group.groupImage,
          groupId: group.id,
          memberCount: group.memberCount,
          description: group.description,
        }))

        // Combine users first, then groups
        setSearchResults([...userResults, ...groupResults])
      } catch (err) {
        console.error('Error searching users:', err)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }

    const timer = setTimeout(searchUsers, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Add team member (user or group)
  const handleAddMember = async (item: SearchResultItem) => {
    if (!adventureId || !currentUserId) {
      setError('Unable to add team member: Missing required information')
      return
    }

    try {
      const roleTypeLower = roleType.toLowerCase()

      // Map string role type to numeric enum value
      let roleTypeEnum: RoleType
      if (roleType === 'Maintainer') {
        roleTypeEnum = RoleType.Maintainer
      } else if (roleType === 'Moderator') {
        roleTypeEnum = RoleType.Moderator
      } else if (roleType === 'Tester') {
        roleTypeEnum = RoleType.Tester
      } else {
        throw new Error(`Unknown role type: ${roleType}`)
      }

      // Build request - use userId for users, groupId for groups
      const request: any = {
        adventureId: adventureId,
        roleType: roleTypeEnum,
        addedByUserId: currentUserId,
        ...(item.type === 'user' ? { userId: item.userId } : { groupId: item.groupId }),
      }

      console.log('Sending request:', request)

      if (roleTypeLower === 'maintainer') {
        await adventuresApi.addMaintainer(adventureId, request)
      } else if (roleTypeLower === 'moderator') {
        await adventuresApi.addModerator(adventureId, request)
      } else if (roleTypeLower === 'tester') {
        await adventuresApi.addTester(adventureId, request)
      }

      await loadTeamMembers()
      setSearchQuery('')
      setSearchResults([])
    } catch (err: any) {
      console.error(`Error adding ${roleType}:`, err)
      console.error('Error response:', err.response?.data)

      // Extract validation errors if available
      const errorData = err.response?.data
      let message = `Failed to add ${roleType.toLowerCase()}`

      if (errorData?.errors) {
        const errorMessages = Object.entries(errorData.errors)
          .map(([field, messages]: [string, any]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ')
        message = errorMessages || message
      } else if (errorData?.message) {
        message = errorData.message
      } else if (errorData?.title) {
        message = errorData.title
      }

      setError(message)
    }
  }

  // Remove team member (user or group)
  const handleRemoveMember = async (member: TeamMember) => {
    if (!adventureId) return
    const itemType = member.isGroup ? 'group' : 'user'
    if (!window.confirm(`Remove this ${itemType} from ${roleType.toLowerCase()}s?`)) return

    try {
      const roleTypeLower = roleType.toLowerCase()
      const memberId = member.memberId

      if (roleTypeLower === 'maintainer') {
        await adventuresApi.removeMaintainer(adventureId, memberId)
      } else if (roleTypeLower === 'moderator') {
        await adventuresApi.removeModerator(adventureId, memberId)
      } else if (roleTypeLower === 'tester') {
        await adventuresApi.removeTester(adventureId, memberId)
      }

      await loadTeamMembers()
      setError(null)
    } catch (err: any) {
      console.error(`Error removing ${roleType}:`, err)
      const message = err.response?.data?.message || `Failed to remove ${roleType.toLowerCase()}`
      setError(message)
    }
  }

  // Check if the current user is already in the team member list
  const isCurrentUserInList = teamMembers.some(
    (m) => m.isUser && m.memberId === currentUserId
  )

  // Add the current user as a team member
  const handleAddSelf = () => {
    if (!currentUser || !currentUserId) return
    handleAddMember({
      id: currentUserId,
      type: 'user',
      displayName: currentUser.displayName,
      profileImageUrl: currentUser.avatarUrl || currentUser.avatar,
      userId: currentUserId,
    })
  }

  if (!adventureId) {
    return (
      <Card>
        <CardHeader avatar={icon} title={title} subheader={description} />
        <CardContent>
          <Alert severity="info">Save the adventure first to manage team members.</Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader avatar={icon} title={title} subheader={description} />
      <CardContent>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Search and Add Section */}
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Add {roleType}
          </Typography>
          <Autocomplete<SearchResultItem, false, false, true>
            freeSolo
            options={searchResults}
            filterOptions={(x) => x} // Disable built-in filtering - we filter via API
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option
              return option.displayName || ''
            }}
            renderOption={(props, option, state) => {
              const { key, ...otherProps } = props
              const isGroup = option.type === 'group'

              return (
                <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    src={option.profileImageUrl}
                    alt={option.displayName}
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: isGroup ? 'primary.main' : undefined,
                    }}
                  >
                    {isGroup ? <GroupIcon /> : option.displayName?.[0]?.toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.displayName}
                      </Typography>
                      {isGroup && (
                        <Chip
                          label="Group"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                    {isGroup ? (
                      <Typography variant="caption" color="textSecondary">
                        {option.memberCount} member{option.memberCount !== 1 ? 's' : ''}
                        {option.description && ` • ${option.description.substring(0, 40)}${option.description.length > 40 ? '...' : ''}`}
                      </Typography>
                    ) : option.rank ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.9rem' }}>
                          {option.rank.badge}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                          {option.rank.name}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              )
            }}
            inputValue={searchQuery}
            onInputChange={(_, value) => setSearchQuery(value)}
            onChange={(_, value) => {
              if (value && typeof value === 'object') {
                handleAddMember(value)
              }
            }}
            loading={isSearching}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={`Search users or groups to add as ${roleType.toLowerCase()}`}
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                  endAdornment: (
                    <>
                      {isSearching ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        {/* Add Myself Button */}
        {!isCurrentUserInList && !isLoading && (
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={handleAddSelf}
            fullWidth
            sx={{ mb: 2 }}
          >
            Add Myself as {roleType}
          </Button>
        )}

        {/* Current Team Members List */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Current {roleType}s ({teamMembers.length})
          </Typography>

          {isLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : teamMembers.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body2" color="textSecondary">
                No {roleType.toLowerCase()}s assigned yet
              </Typography>
            </Box>
          ) : (
            <List>
              {teamMembers.map((member) => (
                <ListItem
                  key={member.id}
                  secondaryAction={
                    !member.isCreator && (
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveMember(member)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      src={member.profileImageUrl}
                      alt={member.displayName}
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: member.isGroup ? 'primary.main' : undefined,
                      }}
                    >
                      {member.isGroup ? <GroupIcon /> : member.displayName?.[0]?.toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {member.displayName}
                        </Typography>
                        {member.isGroup && (
                          <Chip
                            label="Group"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        {member.isCreator && (
                          <Chip label="Creator" size="small" color="primary" sx={{ height: 20 }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box component="span" display="flex" alignItems="center" gap={1}>
                        {member.isGroup ? (
                          <Typography component="span" variant="caption" color="textSecondary">
                            {member.memberCount} member{member.memberCount !== 1 ? 's' : ''}
                            {member.groupDescription && ` • ${member.groupDescription.substring(0, 50)}${member.groupDescription.length > 50 ? '...' : ''}`}
                          </Typography>
                        ) : member.rank ? (
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography component="span" variant="caption" sx={{ fontSize: '0.9rem' }}>
                              {member.rank.badge}
                            </Typography>
                            <Typography component="span" variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                              {member.rank.name}
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Permissions Info */}
        <Box mt={3} p={2} bgcolor="info.light" borderRadius={1}>
          <Typography variant="caption">
            <strong>Permissions:</strong> {getPermissionsText(roleType)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}
